import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

export default function CrewStickinessPanel({ appUserId = null, isOpen = true }) {
  const [loading, setLoading] = useState(true);
  const [waitingForSignIn, setWaitingForSignIn] = useState(false);
  const [crews, setCrews] = useState([]);
  const [selectedCrewId, setSelectedCrewId] = useState('');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState('');
  const [debugHint, setDebugHint] = useState('');

  useEffect(() => {
    let alive = true;
    let authSub = null;

    const loadCrewsFallback = async (authUserId) => {
      let resolvedAppUserId = appUserId || null;
      if (!resolvedAppUserId) {
        const { data: appUserRow, error: appUserErr } = await supabase
          .from('users')
          .select('id')
          .eq('auth_user_id', authUserId)
          .maybeSingle();
        if (appUserErr) throw appUserErr;
        resolvedAppUserId = appUserRow?.id || null;
      }
      if (!resolvedAppUserId) return [];

      const { data: memberRows, error: membersErr } = await supabase
        .from('crew_members')
        .select('crew_id, role, joined_at')
        .eq('user_id', resolvedAppUserId);
      if (membersErr) throw membersErr;

      const normalizedMembers = (memberRows || []).filter((row) => !!row.crew_id);
      const crewIds = normalizedMembers.map((r) => r.crew_id);
      if (crewIds.length === 0) return [];

      const { data: crewRows, error: crewsErr } = await supabase
        .from('crews')
        .select('id, name, emoji')
        .in('id', crewIds);
      if (crewsErr) throw crewsErr;

      const { data: streakRows, error: streakErr } = await supabase
        .from('crew_streaks')
        .select('crew_id, current_streak, last_active_date')
        .in('crew_id', crewIds);
      if (streakErr) throw streakErr;

      const crewMap = new Map((crewRows || []).map((r) => [r.id, r]));
      const streakMap = new Map((streakRows || []).map((r) => [r.crew_id, r]));
      return normalizedMembers.map((row) => ({
        crew_id: row.crew_id,
        role: row.role,
        joined_at: row.joined_at,
        crew_name: crewMap.get(row.crew_id)?.name || 'Crew',
        crew_emoji: crewMap.get(row.crew_id)?.emoji || '👥',
        current_streak: Number(streakMap.get(row.crew_id)?.current_streak || 0),
        last_active_date: streakMap.get(row.crew_id)?.last_active_date || null,
      }));
    };

    const loadCrews = async (attempt = 0) => {
      if (!alive) return;
      setLoading(true);
      setError('');
      if (attempt === 0) setDebugHint('');
      try {
        const { data: sessionRes } = await supabase.auth.getSession();
        const session = sessionRes?.session || null;
        const authUser = session?.user || null;

        if (!authUser?.id) {
          if (!alive) return;
          setWaitingForSignIn(true);
          setLoading(false);
          setDebugHint('Waiting for sign-in…');
          return;
        }

        setWaitingForSignIn(false);
        let rows = [];
        const { data, error: rpcErr } = await supabase.rpc('get_my_crews');
        if (rpcErr) {
          // Fall back to direct reads if RPC auth context isn't ready yet.
          rows = await loadCrewsFallback(authUser.id);
        } else {
          rows = Array.isArray(data) ? data : [];
        }
        if (!alive) return;
        setCrews(rows);
        if (rows.length > 0) {
          setDebugHint('');
          setSelectedCrewId((prev) => (prev && rows.some((r) => r.crew_id === prev) ? prev : rows[0].crew_id));
        } else if (attempt < 2) {
          setDebugHint('No crews returned (will retry)…');
          setTimeout(() => {
            if (alive) loadCrews(attempt + 1);
          }, 500);
          return;
        } else if (!rpcErr) {
          // One last fallback attempt after RPC returned empty but session exists.
          try {
            const fallbackRows = await loadCrewsFallback(authUser.id);
            if (!alive) return;
            if (fallbackRows.length > 0) {
              setCrews(fallbackRows);
              setDebugHint('');
              setSelectedCrewId((prev) => (prev && fallbackRows.some((r) => r.crew_id === prev) ? prev : fallbackRows[0].crew_id));
              return;
            }
          } catch (fallbackErr) {
            console.error('[crew] fallback_load_failed', fallbackErr);
          }
        } else {
          setDebugHint('No crews returned.');
          setSelectedCrewId('');
        }
      } catch (e) {
        console.error('[crew] get_my_crews_failed', e);
        if (alive) setError(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (isOpen) {
      loadCrews(0);
    } else {
      setLoading(false);
    }

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (!alive) return;
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (isOpen) loadCrews(0);
      }
      if (event === 'SIGNED_OUT') {
        setCrews([]);
        setSelectedCrewId('');
        setStats(null);
        setWaitingForSignIn(true);
        setDebugHint('Waiting for sign-in…');
      }
    });
    authSub = listener?.subscription || null;

    return () => {
      alive = false;
      try { authSub?.unsubscribe?.(); } catch {}
    };
  }, [appUserId, isOpen]);

  useEffect(() => {
    let alive = true;
    if (!selectedCrewId) {
      setStats(null);
      return () => {};
    }
    (async () => {
      setStatsLoading(true);
      setError('');
      try {
        const { data, error: rpcErr } = await supabase.rpc('get_crew_stats', { p_crew_id: selectedCrewId });
        if (rpcErr) throw rpcErr;
        if (!alive) return;
        if (!data?.ok) {
          setStats(null);
          if (data?.error && data?.error !== 'join_required') setError(data.error);
          return;
        }
        setStats(data);
      } catch (e) {
        console.error('[crew] get_crew_stats_failed', e);
        if (alive) setError(e?.message || String(e));
      } finally {
        if (alive) setStatsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedCrewId]);

  const topPreview = useMemo(() => (Array.isArray(stats?.top_members_7d) ? stats.top_members_7d.slice(0, 5) : []), [stats]);
  const selectedCrew = useMemo(() => crews.find((crew) => crew.crew_id === selectedCrewId) || null, [crews, selectedCrewId]);

  return (
    <div className="space-y-1.5">
      <div className="font-semibold text-slate-100 flex items-center gap-1.5">
        <span>👥 Crew heat</span>
      </div>
      <p className="text-slate-300">
        See which crew is still arguing, who is carrying the room, and where the heat is actually coming from.
      </p>

      <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-3 space-y-3">
        {loading ? (
          <div className="text-xs text-slate-400">{waitingForSignIn ? 'Waiting for sign-in…' : 'Loading crews…'}</div>
        ) : crews.length === 0 ? (
          <div className="text-xs text-slate-400">
            {waitingForSignIn ? 'Waiting for sign-in…' : 'Join or create a crew to turn verdicts into group rivalries, streaks, and standings.'}
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Pick your arena</div>
              <div className="space-y-1.5 max-h-40 overflow-auto pr-1">
                {crews.map((crew) => {
                  const active = selectedCrewId === crew.crew_id;
                  return (
                    <button
                      key={crew.crew_id}
                      onClick={() => setSelectedCrewId(crew.crew_id)}
                      className={`w-full text-left rounded-lg border px-3 py-2 transition ${active ? 'border-purple-500/60 bg-purple-500/10' : 'border-slate-700 bg-slate-900/40 hover:bg-slate-800/70'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-slate-100 truncate">
                            <span className="mr-1">{crew.crew_emoji || '👥'}</span>
                            {crew.crew_name}
                          </div>
                          <div className="text-[11px] text-slate-400 mt-0.5">{crew.role || 'member'} • {Number(crew.current_streak || 0)} day run</div>
                        </div>
                        <div className="text-[11px] font-semibold text-cyan-200 shrink-0">🔥 live</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedCrewId && (
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3">
                {statsLoading ? (
                  <div className="text-xs text-slate-400">Loading the room…</div>
                ) : stats?.ok ? (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-100 truncate">{stats.crew_emoji || '👥'} {stats.crew_name}</div>
                        <div className="mt-1 text-[11px] text-slate-400">
                          {selectedCrew?.role || 'member'} • who is carrying the takes this week?
                        </div>
                      </div>
                      <Link
                        to={`/crew/${stats.crew_id}`}
                        className="text-[11px] font-semibold text-purple-300 hover:text-purple-200"
                      >
                        Open arena →
                      </Link>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                      <div className="rounded-lg bg-slate-800/70 p-2 border border-slate-700">
                        <div className="text-slate-400">Heat streak</div>
                        <div className="text-slate-100 font-bold">🔥 {stats.current_streak || 0}</div>
                      </div>
                      <div className="rounded-lg bg-slate-800/70 p-2 border border-slate-700">
                        <div className="text-slate-400">Takes cast</div>
                        <div className="text-slate-100 font-bold">{stats.votes_7d || 0}</div>
                      </div>
                      <div className="rounded-lg bg-slate-800/70 p-2 border border-slate-700">
                        <div className="text-slate-400">Voices in</div>
                        <div className="text-slate-100 font-bold">{stats.active_members_7d || 0}</div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Who carried the room (7d)</div>
                      <div className="space-y-1">
                        {topPreview.length === 0 ? (
                          <div className="text-[11px] text-slate-400">No one has really stirred it up yet this week.</div>
                        ) : (
                          topPreview.map((m, idx) => (
                            <div key={`${m.user_id}_${idx}`} className="flex items-center justify-between text-[11px] rounded-md bg-slate-800/50 px-2 py-1">
                              <div className="truncate text-slate-200">{idx + 1}. {m.username || 'Unknown'}</div>
                              <div className="font-semibold text-slate-100">{m.score} pts</div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-slate-400">Pick a crew to see who is actually moving the room.</div>
                )}
              </div>
            )}
          </>
        )}

        {debugHint && !error && <div className="text-[11px] text-slate-400">{debugHint}</div>}
        {error && <div className="text-[11px] text-amber-300">{error}</div>}
      </div>
    </div>
  );
}
